import { useContext, useState } from "react";
import { AuthContext } from "../auth-context";
import { actualAge } from "../utils/calculations";
import { tryOrMessage, yearOfBirthPattern } from "../utils/strings";
import IOList from "../widget/io-list";
import IONumber from "../widget/io-number";
import IOPanelWithNavigation from "../widget/io-panel-with-navigation";
import IOString from "../widget/io-string";
import IOText from "../widget/io-text";
import Panel from "../widget/panel";
import TwoColumns from "../widget/two-columns";
import type { Patient } from "./_objects-patient";

const emptyValue = null;

// district > upazilla > union
const geography: Record<
  string,
  typeof emptyValue | Record<string, typeof emptyValue | string[]>
> = {
  Chittagong: {
    Chandanish: emptyValue,
    Patia: emptyValue,
    Shatkania: emptyValue,
    Lohagora: emptyValue
  },
  "Cox's Bazar": {
    Chakaria: [
      "Harbang",
      "Baraitoly",
      "Kayarbil",
      "Lakkar Char",
      "Kakara",
      "Surajpur",
      "Chiringa",
      "Baniarchhori",
      "Fashia Khali",
      "Dulahazara",
      "Khutakhali",
      "Shaharbil",
      "Purbaboroveola",
      "Paschim Boro Veola",
      "B.M. Char",
      "Badar Khali",
      "Kona Khali",
      "Demoshia",
      "Paurashava"
    ],
    "Cox's Bazar Sadar": [
      "Varoakhali",
      "Chafuldhandi",
      "Eidgaon",
      "Islamabad",
      "Jalalabad",
      "Jilongja",
      "Khoroshkhol",
      "Masuakhali",
      "P.M. Khali",
      "Pukkhali",
      "Cox's Bazar Sadar",
      "Islampur"
    ],
    Ukhia: [
      "Halodia Palang",
      "Ratna Palang",
      "Raja Palang",
      "Jalia Palang",
      "Palang Khali"
    ],
    Ramu: [
      "Chakmar Kul",
      "Kawar Kup",
      "Dakkin Mitachari",
      "Juaria Nala",
      "Photekarkul",
      "Kochchapia",
      "Rasid Nagar",
      "Gorjania",
      "RajarKool",
      "Eidgar",
      "Khonia Palang"
    ],
    Pekua: [
      "Pekua",
      "Barobakia",
      "Shilkhali",
      "Taytang",
      "Magnama",
      "Rajakhali",
      "Ujantia"
    ],
    Kutubdia: emptyValue,
    Moheshkhali: [
      "Dhalghat",
      "Kalamar Chara",
      "Bara Moheshkhali",
      "Hoanak",
      "Shaplapur",
      "Kutobzom",
      "Chota Moheshkhali",
      "Paurashava",
      "Matarbari"
    ],
    Teknaf: [
      "St. Martin",
      "Shabrang",
      "Teknaf Sadar",
      "Paurashava",
      "Bahar Chara",
      "Nhila",
      "Hnila"
    ]
  },
  Bandarban: emptyValue
};

export default function PatientElement(props: {
  file: Patient;
  edit: boolean;
  closed: boolean;
  isProtected: boolean;
  hidden?: boolean;
  onUpdated: (patient: Patient) => void;
  onDeleted: (patient: Patient) => void;
}) {
  const authenticationContext = useContext(AuthContext)!;

  const patient = props.file;

  const [districtValue, districtValueUpdate] = useState<string>(
    patient.address_district ?? ""
  );
  const [upazilaValue, upazilaValueUpdate] = useState<string>(
    patient.address_upazilla ?? ""
  );

  return (
    <IOPanelWithNavigation<Patient>
      {...props}
      key={`patient.${props.file.id}`}
      file={patient}
      type="patient"
      apiRootUrl={`/api/patient`}
      restrictedForWrite="write"
      isProtected={props.isProtected}
      canBeLocked={false}
      onCreated={() => {}} // No create possible
      basePath={`/patient/${props.file.id}/patient`}
      header={
        <>
          <span data-testid="patient-header-entry">
            {patient.entry_year}-{patient.entry_order}
          </span>
          <span className="no-mobile">{patient.name}</span>
          <span className="no-mobile">{patient.year_of_birth}</span>
        </>
      }
    >
      <TwoColumns>
        <Panel fixed label="Identification">
          <IONumber
            label="Entry Year"
            value={patient.entry_year}
            htmlProps={{
              min: 1980,
              max: 2030
            }}
          />
          <IONumber label="Entry Order" value={patient.entry_order} />
          <IOString name="name" value={patient.name} required />
          <IOList
            name="sex"
            value={patient.sex}
            list={authenticationContext.getList("sex")}
          />
          <IOString
            name="year_of_birth"
            label="Year of Birth"
            value={patient.year_of_birth}
            htmlProps={{
              pattern: yearOfBirthPattern
            }}
            inputHelp={<div>YYYY or YYYY-MM - between 1960 and today</div>}
          />
          <IOString
            label="Age today"
            value={tryOrMessage(() => actualAge(patient))}
            e2eExcluded
          />
          <IOList
            name="pathology"
            value={patient.pathology}
            list={authenticationContext.getList("Pathologies")}
          />
          <IOText name="comments" value={patient.comments} />
        </Panel>
        <Panel fixed label="Address">
          <IOString name="phone" value={patient.phone ?? ""} />
          <IOList
            name="address_district"
            label="District"
            value={districtValue}
            list={Object.keys(geography)}
            onChange={(v) => {
              districtValueUpdate(v);
              upazilaValueUpdate("");
            }}
          />
          <IOList
            name="address_upazilla"
            label="Upazila"
            value={upazilaValue}
            list={Object.keys(geography[districtValue] ?? [])}
            onChange={(v) => upazilaValueUpdate(v)}
          />
          <IOList
            name="address_union"
            label="Union"
            value={patient.address_union}
            list={geography[districtValue]?.[upazilaValue] ?? []}
          />
          <IOText name="address_comments" value={patient.address_comments} />
        </Panel>
      </TwoColumns>
    </IOPanelWithNavigation>
  );
}
